import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bw_user") || "null"); } catch { return null; }
  });
  const [expiresAt, setExpiresAt] = useState(() => localStorage.getItem("bw_expires_at") || null);
  const [ready, setReady] = useState(false);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    localStorage.removeItem("bw_token");
    localStorage.removeItem("bw_user");
    localStorage.removeItem("bw_expires_at");
    setUser(null);
    setExpiresAt(null);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("bw_token", data.token);
      localStorage.setItem("bw_user", JSON.stringify(data.user));
      localStorage.setItem("bw_expires_at", data.expires_at);
      setUser(data.user);
      setExpiresAt(data.expires_at);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  // Verify token on mount if present
  useEffect(() => {
    const token = localStorage.getItem("bw_token");
    if (!token) { setReady(true); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); localStorage.setItem("bw_user", JSON.stringify(r.data)); })
      .catch(() => logout())
      .finally(() => setReady(true));
  }, [logout]);

  // Auto-logout on expiry
  useEffect(() => {
    if (!expiresAt) return;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) { logout(); return; }
    const t = setTimeout(() => logout(), ms);
    return () => clearTimeout(t);
  }, [expiresAt, logout]);

  const value = useMemo(() => ({ user, expiresAt, ready, login, logout }), [user, expiresAt, ready, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
